// src/components/Navbar.tsx
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import LinkContainer from "react-bootstrap/Container";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import NavDropdown from "react-bootstrap/NavDropdown";

import { useSelector, useDispatch } from "react-redux";
import { logout } from "../states/slices/authSlice"; // Import the logout action
import { fetchSiteConfig } from "../services/SiteConfigService";
import { apiGet } from "../services/BaseService";
import { getLoggedInUser } from "../services/UserService";

import logo from "../logo.png";

const Header: React.FC = () => {
  const location = useLocation();
  const [homePageMode, setHomePageMode] = useState<string>("advanced");
  const [siteConfig, setSiteConfig] = useState<any>(null);
  const [userFirstName, setUserFirstName] = useState<string>("");

  useEffect(() => {
    const loadConfig = async () => {
      try {
        // First try to use the service function
        try {
          const config = await fetchSiteConfig();
          setHomePageMode(config.homePageMode);
          setSiteConfig(config);
        } catch (serviceError) {
          // Fallback to direct API call if service fails
          try {
            const config = await apiGet('/site-config');
            setHomePageMode(config.homePageMode);
            setSiteConfig(config);
          } catch (error) {
            console.error('Failed to fetch site config:', error);
          }
        }
      } catch (error) {
        console.error("Failed to load site configuration:", error);
      }
    };

    loadConfig();
  }, []);

  const isActive = (path: string) => {
    if (path === "/" && location.pathname === "/") return "active";
    if (path !== "/" && location.pathname.startsWith(path)) return "active";
    return "";
  };

  const isAuthenticated = useSelector(
    (state: any) => state.auth.isAuthenticated
  );
  const menuPermissions = useSelector(
    (state: any) => state.auth.menuPermissions
  );

  useEffect(() => {
    const fetchUserData = async () => {
      if (isAuthenticated) {
        try {
          const userData = await getLoggedInUser();
          setUserFirstName(userData.firstName || "");
        } catch (error) {
          console.error("Failed to fetch user data:", error);
        }
      } else {
        setUserFirstName("");
      }
    };

    fetchUserData();
  }, [isAuthenticated]);

  const convertToSlug = (input: string): string => {
    return input
      .replace("Page", "")
      .replace(/([a-z])([A-Z])/g, "$1-$2")
      .replace(/\s+/g, "-")
      .replace(/[^a-zA-Z0-9-]/g, "")
      .toLowerCase();
  };

  const convertToSpacedFormat = (camelCaseString: string): string => {
    const spacedFormat = camelCaseString
      .replace("Page", "")
      .replace(/([a-z])([A-Z])/g, "$1 $2");
    return spacedFormat.charAt(0).toUpperCase() + spacedFormat.slice(1);
  };

  const dispatch = useDispatch();

  return (
    <Navbar sticky="top" collapseOnSelect={true} bg="light" expand="sm">
      <Nav className="ml-auto">
        <LinkContainer>
          <Nav.Link as={Link} to="/">
            <Navbar.Brand>
              <img
                src={logo}
                alt="Logo"
                width="30"
                height="30"
                className="d-inline-block align-top"
              />
            </Navbar.Brand>
          </Nav.Link>
        </LinkContainer>
      </Nav>
      <Navbar.Toggle aria-controls="basic-navbar-nav" />
      <Navbar.Collapse id="basic-navbar-nav">
        <Nav className="ml-auto">
          <LinkContainer>
            <Nav.Link
              eventKey={1}
              as={Link}
              to="/"
              className={isActive("/")}
            >
              <div className="d-flex align-items-center">
                <i className="bi bi-house-fill" />
                <span className="px-2">Home</span>
              </div>
            </Nav.Link>
          </LinkContainer>
 {homePageMode === "advanced" && (
            <>
              <LinkContainer>
                <Nav.Link
                  eventKey={2}
                  as={Link}
                  to="/schedules"
                  className={isActive("/schedules")}
                >
                  <div className="d-flex align-items-center">
                    <i className="bi bi-calendar-week" />
                    <span className="px-2">Schedules</span>
                  </div>
                </Nav.Link>
              </LinkContainer>
              <LinkContainer>
                <Nav.Link
                  eventKey={3}
                  as={Link}
                  to="/groups"
                  className={isActive("/groups")}
                >
                  <div className="d-flex align-items-center">
                    <i className="bi bi-people-fill" />
                    <span className="px-2">Groups</span>{" "}
                  </div>
                </Nav.Link>
              </LinkContainer>
              <LinkContainer>
                <Nav.Link
                  eventKey={4}
                  as={Link}
                  to="/plays"
                  className={isActive("/plays")}
                >
                  <div className="d-flex align-items-center">
                    <i className="bi bi-stars" />
                    <span className="px-2">Plays</span>{" "}
                  </div>
                </Nav.Link>
              </LinkContainer>
            </>
          )}
          {/* Articles link - always shown */}
          <LinkContainer>
            <Nav.Link
              eventKey={5}
              as={Link}
              to="/articles"
              className={isActive("/articles")}
            >
              <div className="d-flex align-items-center">
                <i className="bi bi-journal-text" />
                <span className="px-2">Articles</span>
              </div>
            </Nav.Link>
          </LinkContainer>
          
          {/* Basic mode content links - shown based on config */}
          {homePageMode === "basic" && (
            <>
              {/* Groups link - always shown in basic mode */}
              <LinkContainer>
                <Nav.Link
                  eventKey={3}
                  as={Link}
                  to="/groups"
                  className={isActive("/groups")}
                >
                  <div className="d-flex align-items-center">
                    <i className="bi bi-people-fill" />
                    <span className="px-2">Groups</span>
                  </div>
                </Nav.Link>
              </LinkContainer>
              
              {/* News link - shown if enabled in config */}
              {(!siteConfig || siteConfig.basicHomeConfig?.showNewsSection !== false) && (
                <LinkContainer>
                  <Nav.Link
                    eventKey={6}
                    as={Link}
                    to="/news"
                    className={isActive("/news")}
                  >
                    <div className="d-flex align-items-center">
                      <i className="bi bi-newspaper" />
                      <span className="px-2">News</span>
                    </div>
                  </Nav.Link>
                </LinkContainer>
              )}
              
              {/* Reviews link - shown if enabled in config */}
              {(!siteConfig || siteConfig.basicHomeConfig?.showReviewsSection !== false) && (
                <LinkContainer>
                  <Nav.Link
                    eventKey={7}
                    as={Link}
                    to="/reviews"
                    className={isActive("/reviews")}
                  >
                    <div className="d-flex align-items-center">
                      <i className="bi bi-star-half" />
                      <span className="px-2">Reviews</span>
                    </div>
                  </Nav.Link>
                </LinkContainer>
              )}
              
              {/* Tutorials link - shown if enabled in config */}
              {(!siteConfig || siteConfig.basicHomeConfig?.showTutorialsSection !== false) && (
                <LinkContainer>
                  <Nav.Link
                    eventKey={8}
                    as={Link}
                    to="/tutorials"
                    className={isActive("/tutorials")}
                  >
                    <div className="d-flex align-items-center">
                      <i className="bi bi-mortarboard" />
                      <span className="px-2">Tutorials</span>
                    </div>
                  </Nav.Link>
                </LinkContainer>
              )}
              
              {/* Videos link - always shown */}
              <LinkContainer>
                <Nav.Link
                  eventKey={9}
                  as={Link}
                  to="/videos"
                  className={isActive("/videos")}
                >
                  <div className="d-flex align-items-center">
                    <i className="bi bi-camera-video" />
                    <span className="px-2">Videos</span>
                  </div>
                </Nav.Link>
              </LinkContainer>
            </>
          )}
          {isAuthenticated && (
            <LinkContainer>
              <Nav.Link
                eventKey={10}
                as={Link}
                to="/dashboard"
                className={isActive("/dashboard")}
              >
                <div className="d-flex align-items-center">
                  <i className="bi bi-speedometer2" />
                  <span className="px-2">Dashboard</span>
                </div>
              </Nav.Link>
            </LinkContainer>
          )}
          {isAuthenticated ? (
            <NavDropdown
              title={              
                <span className="px-2">
                  <i className="bi bi-person-circle" /> {userFirstName || 'Profile'}
                </span>
              }
              id="basic-nav-dropdown"
            >
              <NavDropdown.Item eventKey={11} as={Link} to="/profile">
                My Profile
              </NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Item eventKey={12} onClick={() => dispatch(logout())}>
                Log Out
              </NavDropdown.Item>
            </NavDropdown>
          ) : (
            <LinkContainer>
              <Nav.Link
                eventKey={13}
                as={Link}
                to="/signing"
                className={isActive("/signing")}
              >
                <div className="d-flex align-items-center">
                  <i className="bi bi-person-circle" />
                  <span className="px-2">Login</span>
                </div>
              </Nav.Link>
            </LinkContainer>
          )}
        </Nav>
      </Navbar.Collapse>
    </Navbar>
  );
};

export default Header;
